'use client';

import { useUiMessages } from '@awpmarine/ui/components/ui-messages';
import { Tooltip, TooltipContent, TooltipTrigger } from '@awpmarine/ui/components/ui/tooltip';
import { intlFormatDistance } from 'date-fns';
import * as React from 'react';

export interface TimeAgoProps extends Pick<React.ComponentPropsWithoutRef<'span'>, 'className'> {
  /**
   * The Date to display.
   * An actual Date object or something that can be fed to new Date
   */
  value: Date | number | string;

  /**
   * The locale to use for formatting the time ago string.
   * Leave as `undefined` to use the page's `lang`, or the browser's default where there is none.
   * @default undefined
   */
  locale?: string;

  /**
   * Cutoff in milliseconds to switch to absolute date formatting.
   * @default 7 days.
   */
  cutoff?: number;

  /**
   * Whether to show the tooltip with exact date and time on hover.
   * @default true
   */
  showTooltip?: boolean;

  /**
   * Show the absolute calendar date as the main text, with the relative distance on hover.
   * For planning dates (ETA, inspection date) where the date matters more than how far off it is.
   * @default false
   */
  absolute?: boolean;
}

export function TimeAgo({
  value,
  locale = typeof document === 'undefined' ? undefined : document.documentElement.lang || undefined,
  cutoff = 7 * 24 * 60 * 60 * 1000,
  showTooltip = true,
  absolute = false,
  className,
}: TimeAgoProps) {
  const uiMessages = useUiMessages();
  const date = new Date(value);
  const ms = date.getTime();
  const isValidDate = Number.isFinite(ms);

  // `now` is read fresh on every render; the interval only forces re-renders. Background tabs
  // suspend timers, so stored state would go stale and show newly fetched rows as "in X minutes".
  const [, tick] = React.useReducer((count: number) => count + 1, 0);
  // oxlint-disable-next-line react/purity -- rendering the current time is the point; caching it in state left it stale in suspended background tabs
  const now = Date.now();

  // oxlint-disable-next-line awpmarine/no-manual-memo -- the compiler bails on this component, and it re-renders every second
  const absoluteFormatter = React.useMemo(
    () => new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'short', day: '2-digit' }),
    [locale],
  );

  // oxlint-disable-next-line awpmarine/no-manual-memo -- the compiler bails on this component, and it re-renders every second
  const [localDateTimeFormatter, utcDateTimeFormatter] = React.useMemo(
    () => [
      new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short',
      }),
      new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        timeZone: 'UTC',
        timeZoneName: 'short',
      }),
    ],
    [locale],
  );

  const age = isValidDate ? Math.abs(now - ms) : 0;
  const refreshInterval = age < 60_000 ? 1_000 : age < 3_600_000 ? 60_000 : age < 86_400_000 ? 3_600_000 : 86_400_000;

  React.useEffect(() => {
    if (!isValidDate) return;
    const intervalId = window.setInterval(tick, refreshInterval);
    return () => window.clearInterval(intervalId);
  }, [isValidDate, refreshInterval]);

  if (!isValidDate) return null;

  const relative = intlFormatDistance(date, now, { locale, numeric: 'auto', style: 'long' });
  const text = absolute || age >= cutoff ? absoluteFormatter.format(date) : relative;
  const localDateTime = localDateTimeFormatter.format(date);
  const utcDateTime = utcDateTimeFormatter.format(date);

  if (!showTooltip) return <TimeComponent className={className} text={text} date={date} />;

  return (
    <Tooltip>
      <TooltipTrigger>
        <TimeComponent className={className} text={text} date={date} />
      </TooltipTrigger>
      <TooltipContent className='max-w-none py-2'>
        <div className='grid min-w-64 grid-cols-[auto_1fr] gap-x-3 gap-y-1'>
          {absolute && (
            <>
              <div className='rounded bg-background/10 px-1.5 py-0.5 font-medium tracking-wide uppercase'>
                {uiMessages.timeAgoWhen}
              </div>
              <div>{relative}</div>
            </>
          )}
          <div className='rounded bg-background/10 px-1.5 py-0.5 font-medium tracking-wide uppercase'>
            {uiMessages.timeAgoLocal}
          </div>
          <div>{localDateTime}</div>
          <div className='rounded bg-background/10 px-1.5 py-0.5 font-medium tracking-wide uppercase'>
            {uiMessages.timeAgoUtc}
          </div>
          <div>{utcDateTime}</div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function TimeComponent({
  className,
  date,
  text,
  ...props
}: React.ComponentPropsWithoutRef<'time'> & { date: Date; text: string }) {
  return (
    <time className={className} dateTime={date.toISOString()} suppressHydrationWarning {...props}>
      {text}
    </time>
  );
}
